import { zodResolver } from '@hookform/resolvers/zod'
import {
	type ResidenceManagerData,
	residenceCreateManager
} from '@repo/sdk/gen'
import { zResidenceManagerData } from '@repo/sdk/zod'
import { Button } from '@repo/ui/button'
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from '@repo/ui/dialog'
import { Spinner } from '@repo/ui/spinner'
import { formatError } from '@repo/ui/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { UserPlus } from 'lucide-react'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import type { z } from 'zod'
import { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import {
	ManagerFields,
	managerEmailError
} from '../../-components/manager-fields'
import { residenceIssueMessage } from '../../-lib/residence-form'

// The API's own schema, without the residence and language the page sets.
const managerFormSchema = zResidenceManagerData.pick({
	firstName: true,
	lastName: true,
	email: true
})
type ManagerForm = z.infer<typeof managerFormSchema>

/**
 * "Aggiungi gestore": a new address gets an account and the invite; the
 * address of an existing manager links that account to the residence (D17).
 */
function AddManagerDialog({ residenceId }: { residenceId: string }) {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)

	return (
		<Dialog onOpenChange={setOpen} open={open}>
			<DialogTrigger render={<Button size='sm' variant='outline' />}>
				<UserPlus />
				{t('admin-residences:managers.add')}
			</DialogTrigger>
			<DialogContent>
				<AddManagerForm
					onCreated={() => setOpen(false)}
					residenceId={residenceId}
				/>
			</DialogContent>
		</Dialog>
	)
}

function AddManagerForm({
	residenceId,
	onCreated
}: {
	residenceId: string
	onCreated: () => void
}) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const form = useForm<ManagerForm>({
		resolver: zodResolver(managerFormSchema, {
			error: residenceIssueMessage
		}),
		defaultValues: { firstName: '', lastName: '', email: '' }
	})

	const create = useMutation({
		mutationFn: (data: ResidenceManagerData) =>
			residenceCreateManager({ body: { data } }),
		onSuccess: async (response, data) => {
			// A linked account also shows on its other residences.
			await Promise.all(
				response.data.residenceIds.map((id) =>
					queryClient.invalidateQueries({
						queryKey: makeQueryKeys.residences.managers(id)
					})
				)
			)
			toast.success(
				response.outcome === 'linked'
					? t('admin-residences:managers.linked', {
							name: response.data.name ?? data.email
						})
					: t('admin-residences:managers.invited', {
							email: data.email
						})
			)
			onCreated()
		},
		onError: (error) => {
			const message = managerEmailError(error)
			if (message) {
				form.setError('email', { message: t(message) })
				return
			}
			toast.error(formatError(error))
		}
	})

	return (
		<>
			<DialogHeader>
				<DialogTitle>
					{t('admin-residences:managers.add_title')}
				</DialogTitle>
				<DialogDescription>
					{t('admin-residences:managers.add_description')}
				</DialogDescription>
			</DialogHeader>
			<FormProvider {...form}>
				<form
					id='add-manager'
					noValidate
					onSubmit={form.handleSubmit((data) =>
						create.mutate({ ...data, residenceId, language: 'it' })
					)}
				>
					<ManagerFields />
				</form>
			</FormProvider>
			<DialogFooter>
				<DialogClose render={<Button variant='outline' />}>
					{t('common:cancel')}
				</DialogClose>
				<Button
					disabled={create.isPending}
					form='add-manager'
					type='submit'
				>
					{create.isPending && <Spinner />}
					{t('admin-residences:managers.submit')}
				</Button>
			</DialogFooter>
		</>
	)
}

export { AddManagerDialog }
